import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../api';

const SHAPES = [
  { value: 'round', label: 'Rrethore' },
  { value: 'rectangle', label: 'Drejtkëndëshe' },
  { value: 'sweetheart', label: 'Nusërie' }
];

export default function TableEditor() {
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dragging, setDragging] = useState(null); // { id, offsetX, offsetY }
  const canvasRef = useRef(null);

  const load = useCallback(async () => {
    const [rows, allGuests] = await Promise.all([api.getTables(), api.getGuests()]);
    setTables(rows);
    setGuests(allGuests);
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = tables.find((t) => t.id === selectedId) || null;
  const selectedGuests = selected ? guests.filter((g) => g.table_id === selected.id) : [];

  async function unassignGuest(guestId) {
    await api.updateGuest(guestId, { table_id: null });
    await load();
  }

  async function addTable(shape) {
    const defaults = {
      round: { width: 130, height: 130, seats_count: 8 },
      rectangle: { width: 220, height: 100, seats_count: 10 },
      sweetheart: { width: 160, height: 90, seats_count: 2 }
    }[shape];
    const label = shape === 'sweetheart' ? 'Tavolina e Nuserisë' : `Tavolina ${tables.length + 1}`;
    const { id } = await api.createTable({ label, shape, pos_x: 60, pos_y: 60, ...defaults });
    await load();
    setSelectedId(id);
  }

  function onMouseDownTable(e, table) {
    e.stopPropagation();
    const canvasRect = canvasRef.current.getBoundingClientRect();
    setSelectedId(table.id);
    setDragging({
      id: table.id,
      offsetX: e.clientX - canvasRect.left - table.pos_x,
      offsetY: e.clientY - canvasRect.top - table.pos_y
    });
  }

  function onMouseMove(e) {
    if (!dragging) return;
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newX = Math.max(0, Math.round(e.clientX - canvasRect.left - dragging.offsetX));
    const newY = Math.max(0, Math.round(e.clientY - canvasRect.top - dragging.offsetY));
    setTables((prev) => prev.map((t) => (t.id === dragging.id ? { ...t, pos_x: newX, pos_y: newY } : t)));
  }

  async function onMouseUp() {
    if (!dragging) return;
    const t = tables.find((x) => x.id === dragging.id);
    setDragging(null);
    if (t) await api.updateTable(t.id, { pos_x: t.pos_x, pos_y: t.pos_y });
  }

  async function saveSelected(patch) {
    if (!selected) return;
    const updated = { ...selected, ...patch };
    setTables((prev) => prev.map((t) => (t.id === selected.id ? updated : t)));
    await api.updateTable(selected.id, patch);
  }

  async function removeSelected() {
    if (!selected) return;
    if (!window.confirm(`Fshi "${selected.label}"? Mysafirët e caktuar këtu do të mbeten pa tavolinë.`)) return;
    await api.deleteTable(selected.id);
    setSelectedId(null);
    await load();
  }

  return (
    <div className="editor-layout">
      <div className="editor-toolbar">
        <span className="toolbar-label">Shto tavolinë:</span>
        <button onClick={() => addTable('round')}>+ Rrethore</button>
        <button onClick={() => addTable('rectangle')}>+ Drejtkëndëshe</button>
        <button onClick={() => addTable('sweetheart')}>+ Nusërie</button>
      </div>

      <div className="editor-body">
        <div
          className="editor-canvas"
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onClick={() => setSelectedId(null)}
        >
          {tables.map((t) => (
            <div
              key={t.id}
              className={`canvas-table shape-${t.shape} ${selectedId === t.id ? 'selected' : ''}`}
              style={{ left: t.pos_x, top: t.pos_y, width: t.width, height: t.height }}
              onMouseDown={(e) => onMouseDownTable(e, t)}
            >
              <span>{t.label}</span>
              <small>{t.guests_seated || 0}/{t.seats_count}</small>
            </div>
          ))}
          {tables.length === 0 && (
            <p className="canvas-empty">Skica është bosh. Shtoni tavolinën e parë nga toolbar-i lart.</p>
          )}
        </div>

        <div className="editor-panel">
          {!selected && <p className="panel-hint">Klikoni një tavolinë për ta redaktuar.</p>}
          {selected && (
            <>
              <h3>Redakto Tavolinën</h3>
              <label>
                Emri
                <input
                  value={selected.label}
                  onChange={(e) => saveSelected({ label: e.target.value })}
                />
              </label>
              <label>
                Forma
                <select value={selected.shape} onChange={(e) => saveSelected({ shape: e.target.value })}>
                  {SHAPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label>
                Numri i vendeve
                <input
                  type="number"
                  min={1}
                  value={selected.seats_count}
                  onChange={(e) => saveSelected({ seats_count: Number(e.target.value) })}
                />
              </label>
              <div className="panel-row">
                <label>
                  Gjerësia
                  <input type="number" value={selected.width} onChange={(e) => saveSelected({ width: Number(e.target.value) })} />
                </label>
                <label>
                  Lartësia
                  <input type="number" value={selected.height} onChange={(e) => saveSelected({ height: Number(e.target.value) })} />
                </label>
              </div>
              <p className="panel-occupancy">{selected.guests_seated || 0} nga {selected.seats_count} vende të zëna</p>

              <div className="panel-guests">
                <h4>Mysafirët në këtë tavolinë</h4>
                {selectedGuests.length === 0 && (
                  <p className="panel-hint">Ende s'ka mysafirë të caktuar këtu.</p>
                )}
                {selectedGuests.length > 0 && (
                  <ul className="panel-guests-list">
                    {selectedGuests.map((g) => (
                      <li key={g.id}>
                        <span>{g.full_name}</span>
                        <button
                          className="unassign-btn"
                          title="Hiqe nga kjo tavolinë"
                          onClick={() => unassignGuest(g.id)}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button className="danger" onClick={removeSelected}>Fshi Tavolinën</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
